import type { ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Activity, Boxes, ChartNoAxesCombined, KeyRound, Network, Play, Settings, ServerCog, type LucideIcon } from 'lucide-react';

export type PageId = 'overview' | 'providers' | 'models' | 'keys' | 'playground' | 'routing' | 'logs' | 'settings';

export const pages: Array<{ id: PageId; label: string; icon: LucideIcon }> = [
  { id: 'overview', label: 'Overview', icon: Activity },
  { id: 'providers', label: 'Providers', icon: ServerCog },
  { id: 'models', label: 'Models', icon: Boxes },
  { id: 'keys', label: 'Virtual Keys', icon: KeyRound },
  { id: 'playground', label: 'Playground', icon: Play },
  { id: 'routing', label: 'Routing', icon: Network },
  { id: 'logs', label: 'Logs', icon: ChartNoAxesCombined },
  { id: 'settings', label: 'Settings', icon: Settings },
];

const PAGE_PATHS: Record<PageId, string> = {
  overview: '/',
  providers: '/providers',
  models: '/models',
  keys: '/keys',
  playground: '/playground',
  routing: '/routing',
  logs: '/logs',
  settings: '/settings',
};

export function Shell(props: { page: PageId; children: ReactNode; right?: ReactNode }) {
  const location = useLocation();

  return <div className="shell">
    <aside className="sidebar">
      <div className="brand">
        <div className="brand-mark">A</div>
        <div>
          <strong>AetherProxy</strong>
          <span>Universal AI Gateway</span>
        </div>
      </div>
      <nav>
        {pages.map((page) => {
          const Icon = page.icon;
          const to = PAGE_PATHS[page.id];
          return (
            <NavLink
              key={page.id}
              to={to}
              end={page.id === 'overview'}
              className={({ isActive }) => isActive || (page.id === 'overview' && location.pathname === '/dashboard') ? 'active' : ''}
            >
              <Icon size={17} strokeWidth={1.9} aria-hidden="true" />
              {page.label}
            </NavLink>
          );
        })}
      </nav>
    </aside>
    <main>
      <header className="topbar">
        <div>
          <h1>{pages.find((p) => p.id === props.page)?.label}</h1>
          <p>BYOK providers, virtual keys, routing, cache, guardrails, and usage control.</p>
        </div>
        <div className="topbar-right">{props.right}</div>
      </header>
      {props.children}
    </main>
  </div>;
}

export function Card(props: { title?: string; children: ReactNode; actions?: ReactNode }) {
  return <section className="card">
    {(props.title || props.actions) && <div className="card-head">
      {props.title && <h2>{props.title}</h2>}
      {props.actions}
    </div>}
    {props.children}
  </section>;
}

export function Stat(props: { label: string; value: string | number; note?: string }) {
  return <div className="stat"><span>{props.label}</span><strong>{props.value}</strong>{props.note && <small>{props.note}</small>}</div>;
}

export function Empty(props: { children: ReactNode }) {
  return <div className="empty">{props.children}</div>;
}

export function ErrorBox(props: { error: string | null }) {
  if (!props.error) return null;
  return <div className="error-box">{props.error}</div>;
}

export function Field(props: { label: string; children: ReactNode; hint?: string }) {
  return <label className="field"><span>{props.label}</span>{props.children}{props.hint && <small>{props.hint}</small>}</label>;
}

export function TextInput(props: { value: string; onChange: (value: string) => void; placeholder?: string; type?: string }) {
  return <input type={props.type ?? 'text'} value={props.value} placeholder={props.placeholder} onChange={(event) => props.onChange(event.target.value)} />;
}

export function TextArea(props: { value: string; onChange: (value: string) => void; rows?: number }) {
  return <textarea value={props.value} rows={props.rows ?? 6} onChange={(event) => props.onChange(event.target.value)} />;
}

export function Button(props: { children: ReactNode; onClick?: () => void; type?: 'button' | 'submit'; variant?: 'primary' | 'ghost' | 'danger'; disabled?: boolean }) {
  return <button type={props.type ?? 'button'} disabled={props.disabled} className={props.variant === 'ghost' ? 'ghost' : props.variant === 'danger' ? 'danger' : 'primary'} onClick={props.onClick}>{props.children}</button>;
}

export function Table<T>(props: { items: T[]; columns: Array<{ header: string; render: (item: T) => ReactNode }> }) {
  if (props.items.length === 0) return <Empty>No data yet.</Empty>;
  return <div className="table-wrap"><table><thead><tr>{props.columns.map((c) => <th key={c.header}>{c.header}</th>)}</tr></thead><tbody>{props.items.map((item, index) => <tr key={index}>{props.columns.map((c) => <td key={c.header}>{c.render(item)}</td>)}</tr>)}</tbody></table></div>;
}
